import { BearingsTableStoreProvider } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import type { ProductDetailPageProps } from '@hub/bearing-hub/ProductDetails/ProductDetails';
import { ProductDetails } from '@hub/bearing-hub/ProductDetails/ProductDetails';
import type { DiameterValue } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { Intersect } from '@hub/bearing-hub/common/types';
import { getPublicShopSession } from '@hub/libs/catalog-service';
import { getNodeContactProp } from '@hub/libs/node-contact.helper';
import {
  getLocale,
  WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_HEIGHT,
  WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_WIDTH,
} from '@hub/libs/constants';
import { getSingleProductAttributes, getTeaser } from '@hub/libs/graphql.helper.server';
import { createServerI18n, getIbhLanguageAlternates, getServerSideTranslations } from '@hub/libs/i18n.helper';
import type { Footer, ImageLink } from '@hub/models/api/page-content.types';
import type { LanguageAlternate, ProductDetailsParams } from '@hub/models/data';
import type { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { BEARINGHUB_NODEID_OVERWRITES, DEFAULT_QUANTITY } from '@hub/bearing-hub/common/constants';
import { getSessionPayload } from '@hub/common/Session/session.utils';
import { getSuitableProducts } from '@hub/bearing-hub/ProductDetails/SuitableProducts/getSuitableProducts';
import { getUniqueIbhAssociationProducts } from '@hub/bearing-hub/ProductDetails/SuitableProducts/product-associations.helper.server';
import type { AssociationIbhProductFragmentEnhanced, ProductFragmentEnhanced } from '@hub/models/enhanced-fragments';
import { getArticleDataFromContext } from '@hub/bearing-hub/common/link.factory';
import { getCurrentProductCached, replaceLinks } from '@hub/libs/bearing-hub.helper.server';
import { getUniqueAssociatedProductIds } from '@hub/bearing-hub/ProductDetails/SuitableProducts/product-associations.helper';
import { getInitialLayoutData } from '@hub/page-helpers/getInitialLayoutData';
import { getTechnicalDataCategories } from '@hub/bearing-hub/ProductDetails/ProductTabs/technical-data.factory';
import type { TechnicalDataCategory } from '@hub/common/ProductTabs/tabs/SuitableProductsTab';
import { getTechnicalDataWithAttributeNames } from '@hub/bearing-hub/ProductDetails/AkeneoProductData/product-technical-data';
import { getAttributeDefinitions } from '@hub/bearing-hub/ProductDetails/AkeneoProductData/getAttributeDefinitions';
import type {
  ProductAttribute,
  ProductAttributeUnion,
} from '@hub/bearing-hub/ProductDetails/AkeneoProductData/attribute.helper';
import { Article } from '@hub/bearing-hub/ProductDetails/ArticleData/article';
import { getArticleNumber } from '@hub/bearing-hub/ProductDetails/ArticleData/articleNumber';
import { getCustomArticleDimension } from '@hub/bearing-hub/ProductDetails/ArticleData/articleDimensions';
import { type IbhPartsResponseDto, IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { fetchSimilarParts } from '@hub/bearing-hub/BearingHubPage/parts/fetchSimilarParts';
import { LifetimeParametersStoreProvider } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import type { LabeledAttribute } from '@hub/libs/attribute.helper';

import { Logger } from '@icommerce/logger';
import { fetchShapes } from '@hub/bearing-hub/common/shapes/fetchShapes';
import type { UnitSystem } from '@igus/kopla-domain';
import {
  getMetaDescription,
  getMaterialsDescription,
  getProductTitle,
} from '@hub/bearing-hub/ProductDetails/MaterialsDescription/materialsDescription.helper';

export const getServerSideProps: GetServerSideProps<ProductDetailPageProps, ProductDetailsParams> = async context => {
  if (!context.params || !context.params?.shape) {
    Logger.error({ message: `Missing shape parameter like .../iglid(ur|e)-ibh/[sleeve-bearings]` });
    return { notFound: true };
  }

  if (!context.params.categoryName) {
    Logger.error({ message: `Missing articleNumber parameter like .../iglid(ur|e)-ibh/sleeve-bearings/[GTM]` });
    return { notFound: true };
  }

  const allShapes = await fetchShapes();
  const shapePath = context.params.shape;
  const locale = getLocale(context);
  const i18n = await createServerI18n(locale);

  const { materialName, unitSystem, isCustom, selectedShapeDescriptor, productionMethod } = getArticleDataFromContext(
    context,
    allShapes
  );
  const { params } = context;
  const { categoryName } = params;
  const categoryNameIsValid =
    typeof categoryName === 'string' &&
    (categoryName.split('-')[0] === 'iglide' || categoryName.split('-')[0] === 'iglidur');
  const quantity = DEFAULT_QUANTITY;

  const SSRConfig = await getServerSideTranslations(
    serverSideTranslations,
    locale,
    [
      'bearing-hub/bearingHub',
      'layout/header',
      'toast/toast',
      'layout/contactPersonBox',
      'layout/localeSelection',
      'layout/breadcrumb',
      'layout/general',
      'product/buyBox',
      'product/deliveryInfo',
      'product/productDetails',
      'category/categoryPage',
      'category/productTeaser',
      'free-form-hub/freeFormHubCommon',
      'free-form-hub/materials',
      'free-form-hub/materialsDescription',
    ],
    i18n
  );

  if (!categoryNameIsValid) {
    Logger.error({ message: `Category name "${categoryName}" is invalid` });
    return { notFound: true };
  }

  if (!selectedShapeDescriptor) {
    Logger.error({ message: `Shape not found with parameter shapePath = "${shapePath}"` });
    return { notFound: true };
  }
  const articleNumber = await getArticleNumber(
    selectedShapeDescriptor.shapeKey,
    materialName,
    unitSystem,
    productionMethod,
    context.query?.artnr as string
  );

  if (!articleNumber) {
    Logger.error({
      message: `No default article for selected category: categoryName = "${context.params.categoryName}"`,
    });
    return { notFound: true };
  }

  const session: Session = await getPublicShopSession(getSessionPayload(locale));

  if (!session) {
    Logger.error({ message: `Session could not be retrieved` });
    return { notFound: true };
  }

  const customArticleDimensions = getCustomArticleDimension(context.query?.artnr as string);
  const article = Article({
    articleNumber,
    isCustom,
    quantity,
    shapeKey: selectedShapeDescriptor.shapeKey,
    unitSystem,
    dimensions: customArticleDimensions,
    session,
    productionMethod,
  });

  const articleData = await article.fetchArticleData();

  if (!articleData) {
    Logger.error({ message: `Article data could not be retrieved: articleNumber = "${articleNumber}"` });
    return { notFound: true };
  }

  const dimensions = isCustom ? customArticleDimensions : articleData.dimensions;

  if (selectedShapeDescriptor.shapeKey !== articleData.shape) {
    Logger.error({
      message: `Article not matching with given shape: articleNumber = "${articleNumber}", shape = "${shapePath}"`,
    });
    return {
      notFound: true,
    };
  }

  if (materialName !== articleData.material.id) {
    Logger.error({
      message: `Article not matching with given material: articleNumber = "${articleNumber}", material = "${materialName}"`,
    });
    return {
      notFound: true,
    };
  }

  const { articleId, haveCatalogNumber } = article.getArticleIdToFetchDataFromAkeneo(articleData);
  if (!articleId) {
    Logger.error({ message: `Article id could not be extracted from data: articleNumber = "${articleNumber}"` });
    return { notFound: true };
  }

  if (!unitSystem || !articleData.unitSystem || unitSystem !== (articleData.unitSystem as unknown as UnitSystem)) {
    Logger.error({
      message: `Article not matching with given unitSystem: articleNumber = "${articleNumber}", material = "${unitSystem}"`,
    });
    return {
      notFound: true,
    };
  }

  let AkeneoProductData: ProductFragmentEnhanced;
  try {
    AkeneoProductData = await getCurrentProductCached({
      productId: articleId,
      locale,
      currency: session.currency,
      country: session.country,
      deliveryTimeLocale: locale,
    });
  } catch (error) {
    Logger.error({
      message: `getCurrentProduct failed to load articleNumber = "${encodeURIComponent(articleId)}"`,
    });
  }

  if (!AkeneoProductData) {
    if (!isCustom || haveCatalogNumber) {
      Logger.error({
        message: `Catalog article could not be loaded from akeneo: articleNumber = "${encodeURIComponent(articleId)}"`,
      });
    }

    if (!AkeneoProductData) {
      Logger.error({
        message: `Custom article could not be loaded from akeneo: articleNumber = "${encodeURIComponent(articleId)}"`,
      });
    }
  }

  const akeneoProductDataInnerDiameterKey = AkeneoProductData
    ? Object.keys(AkeneoProductData.attributes).find(key => key === 'attr_shaft_diameter')
    : undefined;

  const akeneoProductDataInnerDiameter =
    akeneoProductDataInnerDiameterKey && AkeneoProductData
      ? (
          (
            AkeneoProductData.attributes as Record<
              string,
              ProductAttribute<ProductAttributeUnion | LabeledAttribute<DiameterValue>>
            >
          )[akeneoProductDataInnerDiameterKey] as LabeledAttribute<DiameterValue>
        )?.value
      : undefined;

  let similarParts: IbhPartsResponseDto[] = [];

  if (isCustom) {
    similarParts = await fetchSimilarParts(
      {
        sid: session.sessionId,
      },
      {
        shape: selectedShapeDescriptor.shapeKey,
        productionMethods: [articleData.productionMethod],
        unitSystem: articleData.unitSystem,
        quantity,
        materials: [articleData.material.id],
        dimensions,
      }
    );
  }

  const akeneoProductDataTechnicalDataEntityKeyKey = AkeneoProductData
    ? Object.keys(AkeneoProductData.attributes).find(key => key === 'webshop_technical_data_attributes')
    : undefined;
  const akeneoProductDataTechnicalDataEntityKey =
    akeneoProductDataTechnicalDataEntityKeyKey && AkeneoProductData
      ? (AkeneoProductData.attributes[akeneoProductDataTechnicalDataEntityKeyKey] as string)
      : undefined;

  let technicalDataCategories: TechnicalDataCategory[] = [];

  try {
    const [{ technicalData, technicalDataAttributeNames }, productAttributes] = await Promise.all([
      getTechnicalDataWithAttributeNames(
        locale === 'de-DE' ? 'de-DE' : 'en-IN',
        false,
        akeneoProductDataTechnicalDataEntityKey
      ),
      getSingleProductAttributes(articleId, locale),
    ]);

    const attributeDefinitions = await getAttributeDefinitions(technicalDataAttributeNames, locale);
    technicalDataCategories = getTechnicalDataCategories(
      technicalData,
      productAttributes as Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
      attributeDefinitions,
      articleData,
      locale,
      unitSystem,
      dimensions,
      isCustom,
      i18n
    );
  } catch (error) {
    Logger.error({
      message: `Technical data could not be loaded for articleNumber = "${articleNumber}"`,
      error: error as Error,
    });
  }

  const nodeContactId = BEARINGHUB_NODEID_OVERWRITES.IBH;

  const contact = await getNodeContactProp(nodeContactId, locale);

  const onlineToolsDefinitions = await getTeaser(
    'onlinetoolsteaser',
    locale,
    WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_WIDTH,
    WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_HEIGHT
  );

  let languageAlternates: LanguageAlternate[] = [];
  if (selectedShapeDescriptor) {
    languageAlternates = getIbhLanguageAlternates(locale, {
      selectedShape: selectedShapeDescriptor,
      productKey: articleNumber,
      materialName,
      unitSystem: articleData.unitSystem as unknown as UnitSystem,
    });
  }

  const ids = AkeneoProductData ? getUniqueAssociatedProductIds([AkeneoProductData] as ProductFragmentEnhanced[]) : [];
  const uniqueAssociationProducts = ids && ids.length > 0 ? await getUniqueIbhAssociationProducts(ids, locale) : {};

  const finalFittingSuitableProducts: Record<string, AssociationIbhProductFragmentEnhanced> = {};

  Object.keys(uniqueAssociationProducts)
    .map(key => uniqueAssociationProducts[key])
    .filter(uniqueAssociationProduct => {
      const uniqueAssociationProductArticleIdKey = Object.keys(uniqueAssociationProduct.attributes).find(
        key => key === 'obje_id'
      );
      const uniqueAssociationProductArticleId = (
        uniqueAssociationProduct.attributes[uniqueAssociationProductArticleIdKey] as LabeledAttribute<string>
      )?.value;

      const uniqueAssociationProductProductCMSIdKey = Object.keys(uniqueAssociationProduct.attributes).find(
        key => key === 'product_ID_CMS'
      );
      const uniqueAssociationProductProductCMSId = (
        uniqueAssociationProduct.attributes[uniqueAssociationProductProductCMSIdKey] as LabeledAttribute<number>
      )?.value;

      const uniqueAssociationOuterDiameterKey = Object.keys(uniqueAssociationProduct.attributes).find(
        key => key === 'attr_outer_diameter_d'
      );
      const uniqueAssociationOuterDiameterValue = (
        (
          uniqueAssociationProduct.attributes as Record<
            string,
            ProductAttribute<ProductAttributeUnion | LabeledAttribute<DiameterValue>>
          >
        )[uniqueAssociationOuterDiameterKey] as LabeledAttribute<DiameterValue>
      )?.value;

      if (isCustom) {
        if (uniqueAssociationOuterDiameterValue?.amount === dimensions.d1) {
          finalFittingSuitableProducts[uniqueAssociationProduct.id] = {
            id: uniqueAssociationProduct.id,
            model_id: uniqueAssociationProduct.model_id,
            assets: uniqueAssociationProduct.assets,
            articleId: parseInt(uniqueAssociationProductArticleId, 10),
            productCmsId: uniqueAssociationProductProductCMSId,
            outerDiameter: uniqueAssociationOuterDiameterValue,
            attributes: uniqueAssociationProduct.attributes,
          };
        }
      } else if (
        akeneoProductDataInnerDiameter &&
        uniqueAssociationOuterDiameterValue?.amount === akeneoProductDataInnerDiameter.amount
      ) {
        finalFittingSuitableProducts[uniqueAssociationProduct.id] = {
          id: uniqueAssociationProduct.id,
          model_id: uniqueAssociationProduct.model_id,
          assets: uniqueAssociationProduct.assets,
          articleId: parseInt(uniqueAssociationProductArticleId, 10),
          productCmsId: uniqueAssociationProductProductCMSId,
          outerDiameter: uniqueAssociationOuterDiameterValue,
          attributes: uniqueAssociationProduct.attributes,
        };
      }
    });

  const suitableProducts = AkeneoProductData
    ? getSuitableProducts(AkeneoProductData, finalFittingSuitableProducts, locale, true)
    : [];

  const layoutData = await getInitialLayoutData(locale, languageAlternates);

  (layoutData?.footer as Footer)?.subscribe?.links?.forEach(replaceLinks);
  (layoutData?.footer as Footer)?.payment?.links?.forEach(replaceLinks);
  (layoutData?.footer as Footer)?.payment?.secondRow?.forEach(replaceLinks);
  (layoutData?.footer as Footer)?.awards?.links?.forEach((link: ImageLink) => replaceLinks(link));

  const akeneoProductDataOnlineToolsKey = AkeneoProductData
    ? Object.keys(AkeneoProductData.attributes).find(key => key === 'online_tools')
    : undefined;
  const akeneoProductDataOnlineTools =
    akeneoProductDataOnlineToolsKey && AkeneoProductData
      ? (AkeneoProductData.attributes[akeneoProductDataOnlineToolsKey] as LabeledAttribute<string[]>)?.value
      : undefined;

  const akeneoProductDataDescriptionKey = AkeneoProductData
    ? Object.keys(AkeneoProductData.attributes).find(key => key === 'attr_description')
    : undefined;
  const akeneoProductDataDescription =
    akeneoProductDataDescriptionKey && AkeneoProductData
      ? (AkeneoProductData.attributes[akeneoProductDataDescriptionKey] as LabeledAttribute<string>)?.value
      : undefined;

  const akeneoProductDataSingleUSPKey = AkeneoProductData
    ? Object.keys(AkeneoProductData.attributes).find(key => key === 'attr_single_usp')
    : undefined;
  const akeneoProductDataSingleUSP =
    akeneoProductDataSingleUSPKey && AkeneoProductData
      ? (AkeneoProductData.attributes[akeneoProductDataSingleUSPKey] as LabeledAttribute<string>)?.value
      : undefined;

  const akeneoProductDataUSPKey = AkeneoProductData
    ? Object.keys(AkeneoProductData.attributes).find(key => key === 'attr_USP')
    : undefined;
  const akeneoProductDataUSP =
    akeneoProductDataUSPKey && AkeneoProductData
      ? (AkeneoProductData.attributes[akeneoProductDataUSPKey] as LabeledAttribute<string>)?.value
      : undefined;
  const akeneoProductTitle = (AkeneoProductData?.attributes.series_description as LabeledAttribute<string>)?.value;
  const materialsDescriptionMaterialCode =
    articleData.productionMethod === IbhProductionMethods.SEMIFINISHED
      ? articleData.material.id.replace(/-CNC$/i, '')
      : articleData.material.id;
  const materialsDescription = await getMaterialsDescription({
    materialCode: materialsDescriptionMaterialCode,
    locale,
    shapeKey: selectedShapeDescriptor.shapeKey,
    unitSystem,
    onError: error => {
      Logger.error({
        message: `Materials description could not be loaded for materialCode = "${materialsDescriptionMaterialCode}"`,
        error,
      });
    },
  });
  const productTitle = getProductTitle({
    akeneoTitle: akeneoProductTitle,
    materialName: articleData.material.name,
    shapeKey: selectedShapeDescriptor.shapeKey,
    unitSystem,
    t: i18n.t,
  });
  const metaTitle = materialsDescription.metaTitle || productTitle;
  const metaDescription = getMetaDescription({
    materialMetaDescription: materialsDescription.metaDescription,
    shapeKey: selectedShapeDescriptor.shapeKey,
    t: i18n.t,
  });

  const props: ProductDetailPageProps = {
    languageAlternates,
    onlineToolsDefinitions,
    selectedShape: selectedShapeDescriptor,
    layout: layoutData,
    contact,
    articleNumber,
    articleData,
    akeneoProductData: {
      ...AkeneoProductData,
      onlineTools: akeneoProductDataOnlineTools || null,
      description: akeneoProductDataDescription || null,
      singleUsp: akeneoProductDataSingleUSP || null,
      usp: akeneoProductDataUSP || null,
      innerDiameter: akeneoProductDataInnerDiameter || null,
      title: akeneoProductTitle || null,
    },
    dimensions,
    isCustom,
    technicalDataCategories,
    suitableProducts: suitableProducts.filter(
      suitableProduct => suitableProduct?.title && suitableProduct.usp && suitableProduct.image
    ),
    similarParts,
    productTitle,
    metaTitle,
    metaDescription,
    ...SSRConfig,
  };

  if (materialsDescription.materialDescription) {
    props.materialDescription = materialsDescription.materialDescription;
  }

  return {
    props,
  };
};

const ProductDetailsPage = ({
  languageAlternates,
  onlineToolsDefinitions,
  layout,
  selectedShape,
  contact,
  articleNumber,
  articleData,
  akeneoProductData,
  dimensions,
  isCustom,
  technicalDataCategories,
  suitableProducts,
  similarParts,
  productTitle,
  metaTitle,
  metaDescription,
  materialDescription,
}: ProductDetailPageProps) => (
  <BearingsTableStoreProvider initialData={{ selectedShape }}>
    <LifetimeParametersStoreProvider unitSystem={articleData.unitSystem as unknown as UnitSystem}>
      <ProductDetails
        onlineToolsDefinitions={onlineToolsDefinitions}
        layout={layout}
        selectedShape={selectedShape}
        contact={contact}
        articleNumber={articleNumber}
        articleData={articleData}
        akeneoProductData={akeneoProductData}
        dimensions={dimensions}
        isCustom={isCustom}
        technicalDataCategories={technicalDataCategories}
        suitableProducts={suitableProducts}
        similarParts={similarParts}
        languageAlternates={languageAlternates}
        productTitle={productTitle}
        metaTitle={metaTitle}
        metaDescription={metaDescription}
        materialDescription={materialDescription}
      />
    </LifetimeParametersStoreProvider>
  </BearingsTableStoreProvider>
);

export default ProductDetailsPage;
